<template>
  <div id="pageItemList">
    <v-dialog
      v-model="value.show"
      @input="v => v || closeAction()"
      max-width="550px"
      :persistent="true"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{
            value.mode === "edit" ? $t("editIssue") : $t("createIssue")
          }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="editCreateForm">
            <v-container grid-list-sm>
              <v-layout wrap justify-space-between>
                <v-flex xs12>
                  <v-autocomplete
                    name="vehicles"
                    :label="$t('vehicle')"
                    :loading="vehicleIsLoading"
                    :items="vehicles"
                    :search-input.sync="vehicleSearch"
                    v-model="selectedVehicles"
                    cache-items
                    :clearable="value.mode === 'create'"
                    :chips="value.mode === 'create'"
                    prepend-icon="train"
                    item-text="name"
                    item-value="id"
                    v-validate="'required'"
                    :data-vv-as="$t('vehicle')"
                    :error-messages="errors.collect('vehicles')"
                    :multiple="value.mode === 'create'"
                  >
                    <template
                      v-slot:prepend-item
                      v-if="value.mode === 'create'"
                    >
                      <v-list-tile ripple @click="toggleSelectAllVehicles">
                        <v-list-tile-action>
                          <v-icon
                            :color="
                              selectedVehicles.length > 0
                                ? 'indigo darken-4'
                                : ''
                            "
                            >{{ allVehiclesIcon }}</v-icon
                          >
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>{{
                            $t("selectAllVehicles")
                          }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs6>
                  <v-menu
                    :close-on-content-click="false"
                    v-model="menu"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      name="occurrence_date"
                      slot="activator"
                      :value="
                        issue.occurrence_date
                          ? $moment(issue.occurrence_date).format('LL')
                          : null
                      "
                      :label="$t('occurrenceDate')"
                      v-validate="'required'"
                      :data-vv-as="$t('occurrenceDate')"
                      :error-messages="errors.collect('occurrence_date')"
                      readonly
                    ></v-text-field>
                    <v-date-picker
                      v-model="issue.occurrence_date"
                      @input="menu = false"
                      :locale="LANG"
                      reactive
                      :max="$moment().format('YYYY-MM-DD')"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs6>
                  <v-select
                    name="occurrence_place"
                    v-model="issue.occurrence_place"
                    :items="getAllIssueOccurrencePlaces()"
                    :label="$t('occurrencePlace')"
                    v-validate="'required'"
                    :data-vv-as="$t('occurrencePlace')"
                    :error-messages="errors.collect('occurrence_place')"
                  ></v-select>
                </v-flex>
                <v-flex
                  xs6
                  offset-sm6
                  v-if="issue.occurrence_place === 'OTHER'"
                >
                  <v-text-field
                    name="occurrence_place_custom"
                    :label="$t('occurrencePlaceCustom')"
                    type="text"
                    v-validate="
                      issue.occurrence_place === 'OTHER'
                        ? 'required|max:255'
                        : 'max:255'
                    "
                    v-model="issue.occurrence_place_custom"
                    :data-vv-as="$t('occurrencePlaceCustom')"
                    :error-messages="errors.collect('occurrence_place_custom')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    name="item"
                    :label="$t('issueItem')"
                    type="text"
                    v-model="issue.item"
                    v-validate="'required|max:255'"
                    :data-vv-as="$t('issueItem')"
                    :error-messages="errors.collect('item')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-textarea
                    name="item_additional_info"
                    :label="$t('issueItemAdditionalInfo')"
                    v-model="issue.item_additional_info"
                    :data-vv-as="$t('issueItemAdditionalInfo')"
                    :error-messages="errors.collect('item_additional_info')"
                    auto-grow
                    rows="2"
                  ></v-textarea>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    name="contact_person"
                    :label="$t('contactPerson')"
                    type="text"
                    v-model="issue.contact_person"
                    v-validate="'required|max:255'"
                    :data-vv-as="$t('contactPerson')"
                    :error-messages="errors.collect('contact_person')"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            flat
            @click.native="closeAction"
            :disabled="formDisabled"
            >{{ $t("close") }}</v-btn
          >
          <v-btn
            color="blue darken-1"
            flat
            @click.native="saveAction"
            :disabled="formDisabled"
            >{{
              value.mode === "edit" ? $t("editIssue") : $t("createIssue")
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  UPDATE_ISSUE,
  CREATE_ISSUE,
  RESET_ISSUE,
  SHOW_SNACKBAR,
  FETCH_VEHICLES
} from "@/store/actions.type";
import _ from "lodash";
import validationErrorsMixin from "@/mixins/validationErrors.mixin";
import IssueOccurrencePlace from "@/common/enums/issueOccurrencePlace.service";

export default {
  props: ["value"],
  data() {
    return {
      formDisabled: false,
      vehicleSearch: "",
      menu: false,
      LANG: process.env.VUE_APP_LANG,
      selectedVehicles: []
    };
  },
  computed: {
    ...mapGetters(["issue", "vehicles", "vehiclesCount", "vehicleIsLoading"]),
    selectedAllVehicles() {
      return this.selectedVehicles.length === this.vehicles.length;
    },
    likesSomeVehicles() {
      return this.selectedVehicles.length > 0 && !this.selectedAllVehicles;
    },
    allVehiclesIcon() {
      if (this.selectedAllVehicles) return "check_box";
      if (this.likesSomeVehicles) return "indeterminate_check_box";
      return "check_box_outline_blank";
    }
  },
  mixins: [validationErrorsMixin],
  watch: {
    vehicleSearch: _.debounce(function(val) {
      this.$store.dispatch(FETCH_VEHICLES, [{ rowsPerPage: -1 }, val]);
    }, 300),
    value: {
      handler: function() {
        if (this.value.show === true && this.value.mode === "edit") {
          this.selectedVehicles = this.issue.vehicle;
        }
      },
      deep: true
    }
  },
  methods: {
    saveAction() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.formDisabled = true;

          if (this.value.mode === "edit") {
            this.issue.vehicle = this.selectedVehicles;

            if (_.isObject(this.issue.vehicle)) {
              this.issue.vehicle = this.issue.vehicle.id;
            }
          } else if (this.value.mode === "create") {
            this.issue.vehicles = { ...this.selectedVehicles };
          }
          this.$store
            .dispatch(
              this.value.mode === "edit" ? UPDATE_ISSUE : CREATE_ISSUE,
              this.issue
            )
            .then(() => {
              this.$store.dispatch(SHOW_SNACKBAR, {
                text: this.$t("savedSuccessfully", [this.$t("issue")]),
                color: "success"
              });

              this.closeAction();
              this.$emit("saved");
            })
            .catch(() => {
              this.formDisabled = false;
            });
        }
      });
    },
    closeAction() {
      this.$emit("input", {
        mode: this.value.mode,
        show: false
      });
      this.formDisabled = false;
      // we need wait for animation until is finished
      setTimeout(() => {
        this.$store.dispatch(RESET_ISSUE);
        this.$refs.editCreateForm.reset();
        this.$validator.reset();
        this.selectedVehicles = [];
      }, 100);
    },
    getAllIssueOccurrencePlaces() {
      return IssueOccurrencePlace.getAll();
    },
    toggleSelectAllVehicles() {
      if (this.selectedAllVehicles) {
        this.selectedVehicles = [];
      } else {
        this.selectedVehicles = this.vehicles.map(vehicle => {
          return vehicle.id;
        });
      }
    }
  }
};
</script>

<style scoped></style>
